import type { WeaponKey } from "../../weaponUpgrades";
import { MAX_WEAPON_LEVEL, WEAPON_KEYS, WEAPON_LEVEL_MULTIPLIER, weaponLevel } from "../../weaponUpgrades";
import { triggerSave } from "../persistence";
import { state } from "../state";
import type { ScreenContext } from "../app";
import { shipIconHtml } from "../shipIcon";

// Ship Card (design doc Section 12, 16.30's "one reusable fitting/detail
// component" -- LOCKED as a concept, but the doc's own spec is a full
// module-fitting editor: open Hardpoint/Utility slots capped per-hull, a
// crew stepper per slot, Assign Captain, "Hire Max Crew" staffing every
// module to full effectiveness, and a Fuel/Provisions capacity readout.
// None of that has a data model to show -- PlayerShip is a fixed
// aggregate-stat roster entry (types.ts's own comment: "no build-your-own
// fitting system," 16.16), with no slots/fitted-modules structure, and no
// Fuel/Provisions consumption exists anywhere yet (fleetScreen.ts already
// flags the same gap). Rather than fake any of that, this shows exactly
// what's real -- the roster's own stat grid, plus (for an owned instance)
// its real per-weapon Armory upgrade levels (16.36/16.39, read-only here;
// buying an upgrade stays the Base/Station Armory's job, lotViewScreen.ts
// -- this is a reference view, not a second place to spend credits) --
// and explicitly names the rest as not modeled, per CLAUDE.md's own
// convention of flagging gaps honestly instead of inventing controls with
// nothing behind them.
//
// Reachable as a pane from Fleet roster's "View" (any owned/hired ship,
// with its own weapon levels) and Shipyard's browsing "View" (a hull not
// yet owned, instanceId null -- no upgrade levels exist to show). The
// doc's hull-browsing "View" is a hull-art popup with no other function;
// no art assets exist for any of the 96 ships, so showing the ship's real
// stats here is a strictly more useful substitution, not a shortcut.
//
// Sell is real too (16.45): Section 12's header spec puts Buy/Sell here,
// not on the Shipyard browsing list -- Buy stays exactly where it already
// was (Shipyard's own real credits-gated purchase into an owned entry),
// since duplicating it here risks two purchase implementations drifting
// apart; Sell only makes sense for an already-owned ship (instanceId
// real) anyway. Refund is 35% of the roster's real costCr (Section 12's
// own "30-40%" range, no single number locked), credited and the fleet
// entry removed outright. Module Bay (stripped modules returning to a
// Base) stays deferred -- 16.16 keeps the fitting system out of this
// roster, so there's nothing fitted to strip. Selling the ship currently
// being flown is blocked -- the doc never resolves what happens to a
// captain flying the exact hull they just sold out from under
// themselves, and disallowing it outright is safer than guessing.

const WEAPON_LABELS: Record<WeaponKey, string> = {
  laser: "Laser Turret",
  auto: "Auto-Turret",
  missile: "Missile Turret"
};

const SELL_REFUND_FRACTION = 0.35; // Section 12's own "30-40%" range, no single number locked

export function renderShipCardScreen(container: HTMLElement, ctx: ScreenContext): void {
  const target = state.shipCardTarget ?? (state.currentShip ? { ship: state.currentShip, instanceId: state.currentShipInstanceId } : null);

  const wrap = document.createElement("div");
  container.appendChild(wrap);

  function render(): void {
    if (!target) {
      wrap.innerHTML = '<h1>Ship Card</h1><p class="dim">No ship selected.</p>';
      return;
    }

    const { ship, instanceId } = target;
    const ehp = ship.hullHp + ship.shieldHp;
    const isFlying = !!instanceId && instanceId === state.currentShipInstanceId;
    const refund = Math.round(ship.costCr * SELL_REFUND_FRACTION);

    wrap.innerHTML = `
      <h1 style="display:flex; align-items:center; gap:0.75rem;">
        ${shipIconHtml(ship, "lg")}
        ${ship.name}
      </h1>
      <p class="dim">
        <span class="pill">${ship.variant}</span>
        <span class="pill">${ship.hullTierLabel} (${ship.hullTier})</span>
        <span class="pill">${ship.role}</span>
        ${instanceId ? "" : '<span class="pill">not owned</span>'}
      </p>

      <div class="ship-card-stats dim" style="font-size:1rem; line-height:1.8;">
        Hull HP ${ship.hullHp.toLocaleString()} &middot;
        Shield HP ${ship.shieldHp.toLocaleString()}${ship.shieldRegenPerS !== null ? ` (${ship.shieldRegenPerS.toFixed(1)}/s regen)` : ""} &middot;
        EHP ${ehp.toLocaleString()}<br/>
        Repair ${ship.repairPerS.toFixed(1)}/s &middot;
        DPS ${ship.dps.toFixed(1)}<br/>
        Cargo ${ship.cargoM3.toLocaleString()} m3 &middot;
        Ore Hold ${ship.oreHoldM3.toLocaleString()} m3<br/>
        Crew ${ship.crew} &middot;
        Cost ${ship.costCr.toLocaleString()} CR + ${ship.costResources.toLocaleString()} resources
      </div>

      ${weaponSectionHtml(ship.dps > 0, instanceId)}

      ${
        instanceId
          ? `<div class="cargo-actions" style="margin-top:1rem;">
               <span class="dim">${isFlying ? "Can't sell the ship you're currently flying." : `Sell for ${refund.toLocaleString()} CR (35% refund)`}</span>
               <button type="button" class="btn-danger" data-sell ${isFlying ? "disabled" : ""}>Sell</button>
             </div>`
          : ""
      }

      <p class="dim" style="margin-top:1.5rem;">
        <strong>Not modeled yet:</strong> open Hardpoint/Utility slots, per-slot
        fitting and crew steppers, Assign Captain, Hire Max Crew, and
        Fuel/Provisions capacity -- all of it needs the module-fitting system
        16.16 deliberately keeps out of this roster, or a Fuel/Provisions
        consumption model that doesn't exist yet either.
      </p>
    `;

    wrap.querySelector<HTMLButtonElement>("[data-sell]")?.addEventListener("click", () => {
      sellShip(instanceId!, ship.name, refund);
    });
  }

  function sellShip(instanceId: string, name: string, refund: number): void {
    if (instanceId === state.currentShipInstanceId) return;
    state.fleet = state.fleet.filter((f) => f.id !== instanceId);
    state.credits += refund;
    state.lastEvent = `Sold ${name} for ${refund.toLocaleString()} CR.`;
    state.shipCardTarget = null;
    triggerSave(ctx);
    ctx.navigate("fleet");
  }

  render();
}

function weaponSectionHtml(hasWeapons: boolean, instanceId: string | null): string {
  if (!hasWeapons) {
    return '<p class="dim" style="margin-top:1rem;">No combat Hardpoints (mining/hauling hulls carry none).</p>';
  }
  if (!instanceId) {
    return '<p class="dim" style="margin-top:1rem;">Weapon upgrade levels start at 1 once this hull is bought and flown -- buy it at the Shipyard, upgrade it at a Base/Station Armory.</p>';
  }
  return `
    <div class="cargo-list" style="margin-top:1rem;">
      ${WEAPON_KEYS.map((key) => {
        const level = weaponLevel(state.weaponUpgrades, instanceId, key);
        const mult = WEAPON_LEVEL_MULTIPLIER[level];
        return `
          <div class="cargo-row">
            <span>${WEAPON_LABELS[key]}</span>
            <span class="dim">Level ${level}${level >= MAX_WEAPON_LEVEL ? " (max)" : ""} &middot; +${Math.round((mult - 1) * 100)}% DPS</span>
          </div>
        `;
      }).join("")}
    </div>
    <p class="dim">Read-only here -- upgrade at a Base/Station's Armory. Resets to level 1 if this ship is destroyed.</p>
  `;
}
